import * as React from 'react'
import './styles.scss'
import { useEffect, useState } from 'react'
import SearchBar from '../components/core/SearchBar'
import { WorkerGrid } from '../components/reports/shift-end/workersummary/WorkerGrid'
import {
	confirmTimeSheet,
	createTimesheet,
	revertTimesheet,
} from '../services/shiftEndReportService'
import ConfirmDialog from '../components/core/ConfirmDialog'
import { showSuccessToast } from '../services/CommonServices/alertServices'
import { useShiftEndContext } from '../components/reports/shift-end/ShiftEndContext'
import { ApiBaseResponse } from '../services/CommonServices/httpService'
import { useParams } from 'react-router-dom'
import { useDecodeToken } from '../security/useDecodeToken'
import {
	Box,
	Button,
	CircularProgress,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Tooltip,
	Typography,
} from '@mui/material'
import { useIndexedDB } from '../components/hooks/useIndexedDB'
import { removeTimesheet } from '../services/shiftEndReportService'
import AddTasksDialog from '../components/reports/shift-end/workersummary/manualTimesheet/AddTasksDialog'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { SmallWorker } from '../types/Employee/Employee'

const ShiftEndWorkersPartialPage = () => {
	const token = useDecodeToken()
	const params = useParams()
	const viewAsId = token?.role === 'Foreman' ? Number(token.nameid) : Number(params.id)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false)
	const [addTaskToTimesheetId, setAddTaskToTimesheetId] = useState<{
		timeSheetId: number
		timeRecordId?: number
	} | null>(null)
	const { shiftDate, filteredData, loadData, setSearchText, setIncludeAllTasks, workerSummary } =
		useShiftEndContext()
	const [tsSelectMode, setTsSelectMode] = useState<'select' | null>(null)
	const db = useIndexedDB()
	const [selectedWorkerIds, setSelectedWorkerIds] = useState<number[]>([])
	const [selectedCopyFromLaborerId, setSelectedCopyFromLaborerId] = useState<number>()
	const [laborers, setLaborers] = useState<SmallWorker[]>()

	const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState<boolean>(false)
	const [clickedTimeSheet, setClickedTimeSheet] = useState<number>(0)

	const handleConfirm = async () => {
		
		setIsConfirmLoading(true)
		try {
			const result: ApiBaseResponse = await confirmTimeSheet(clickedTimeSheet, db)
			if (result.success) {
				showSuccessToast('Timesheet Confirmed')
				loadData(viewAsId)
			}
			setIsConfirmDialogVisible(false)
		} finally {
			setIsConfirmLoading(false)
		}
	}

	const handleCopyTimesheets = async () => {
		setIsConfirmLoading(true)
		try {
			await createTimesheet(selectedWorkerIds, shiftDate, viewAsId, selectedCopyFromLaborerId)
			await loadData(viewAsId)
			setLaborers(undefined)
			setSelectedCopyFromLaborerId(undefined)
			setSelectedWorkerIds([])
			setTsSelectMode(null)
			showSuccessToast('Timesheets Copied')
		} finally {
			setIsConfirmLoading(false)
		}
	}

	const showConfirmDialog = (timeSheetId: number) => {
		setClickedTimeSheet(timeSheetId)
		setIsConfirmDialogVisible(true)
	}

	const handleOverride = async (timeSheetId: number) => {
		await revertTimesheet(timeSheetId)
		await loadData(viewAsId)
	}

	const handleRemoveTimesheet = async (timesheetId: number) => {
		await removeTimesheet(timesheetId)
		await loadData(viewAsId)
	}

	useEffect(() => {
		;(async () => {
			setIsLoading(true)
			await loadData(viewAsId)
			setIsLoading(false)
		})()
	}, [shiftDate])

	const handleOnSearchChange = (searchText: string, includeAllTasks: boolean) => {
		setSearchText(searchText)
		setIncludeAllTasks(includeAllTasks)
	}

	return isLoading ? (
		<CircularProgress color='success' />
	) : (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<SearchBar
					onChange={handleOnSearchChange}
					searchPlaceholder='Filter Timesheets'
					showIncludeFilter={false}
				/>
				<Button
					variant='outlined'
					size='small'
					color='primary'
					sx={{
						color: 'primary.main',
						height: '2rem',
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
					}}
					onClick={() => {
						setTsSelectMode(tsSelectMode !== 'select' ? 'select' : null)
						setSelectedWorkerIds([])
					}}
				>
					{tsSelectMode === 'select' ? 'Deselect' : 'Select'}
					{tsSelectMode === 'select' && selectedWorkerIds.length > 0 && (
						<Box
							sx={{
								backgroundColor: 'primary.main',
								color: 'white',
								borderRadius: '50%',
								minWidth: '20px',
								height: '20px',
								display: 'flex',
								alignItems: 'baseline',
								justifyContent: 'center',
								fontSize: '0.75rem',
								fontWeight: 'bold',
							}}
						>
							{selectedWorkerIds.length}
						</Box>
					)}
				</Button>
				{tsSelectMode === 'select' && selectedWorkerIds.length > 0 && (
					<Tooltip title='Copy Timesheets'>
						<Button
							variant='outlined'
							size='small'
							color='primary'
							startIcon={<ContentCopyIcon />}
							sx={{
								height: '2rem',
								display: 'flex',
								alignItems: 'center',

								'&:hover': {
									opacity: 1,
								},
								color: 'primary.main',
							}}
							onClick={() =>
								setLaborers(
									workerSummary
										.filter((ts) => ts.activities.length > 0)
										.map((ts) => ({ id: ts.workerId!, fullName: ts.title })),
								)
							}
						>
							Copy From
						</Button>
					</Tooltip>
				)}
			</Box>
			<WorkerGrid
				tsSelectMode={tsSelectMode}
				selectedWorkerIds={selectedWorkerIds}
				setSelectedWorkerIds={setSelectedWorkerIds}
				timesheets={filteredData}
				openConfirmDialog={showConfirmDialog}
				handleOverride={handleOverride}
				handleRemoveTimesheet={handleRemoveTimesheet}
				handleAddTasks={(timeSheetId: number, timeRecordId?: number) =>
					setAddTaskToTimesheetId({ timeSheetId, timeRecordId })
				}
			/>
			<ConfirmDialog
				title={'Confirm Timesheet'}
				cancelButtonText={'Cancel'}
				confirmButtonText={'Confirm'}
				confirmButton={
					<Button
						variant='contained'
						color='success'
						onClick={handleConfirm}
						disabled={isConfirmLoading}
					>
						{isConfirmLoading ? (
							<CircularProgress size={20} color='inherit' sx={{ m: 0, p: 0 }} />
						) : (
							'Confirm'
						)}
					</Button>
				}
				visible={isConfirmDialogVisible}
				onCancel={() => setIsConfirmDialogVisible(false)}
				onConfirm={handleConfirm}
			>
				Confirm timesheet for the selected worker. Once timesheet is submitted it CANNOT be changed.
			</ConfirmDialog>
			<AddTasksDialog
				open={addTaskToTimesheetId !== null}
				onClose={() => {
					setAddTaskToTimesheetId(null)
					loadData(viewAsId)
				}}
				foremanId={viewAsId}
				timesheetId={addTaskToTimesheetId?.timeSheetId ?? 0}
				beforeTimeRecordId={addTaskToTimesheetId?.timeRecordId}
			/>

			<ConfirmDialog
				title={'Copy Timesheets'}
				cancelButtonText={'Cancel'}
				confirmButtonText={'Copy'}
				confirmButton={
					<Button
						variant='contained'
						color='success'
						onClick={handleCopyTimesheets}
						disabled={isConfirmLoading}
					>
						{isConfirmLoading ? (
							<CircularProgress size={20} color='inherit' sx={{ m: 0, p: 0 }} />
						) : (
							'Copy'
						)}
					</Button>
				}
				visible={!!laborers}
				onCancel={() => setLaborers(undefined)}
				onConfirm={handleCopyTimesheets}
			>
				<Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
					<Typography variant='body1' color='text.primary' sx={{ mb: 3 }}>
						This action overrides any existing timesheets for the selected workers.
					</Typography>
					<Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
						<ContentCopyIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
						Copy timesheet from
					</Typography>
					<FormControl fullWidth size='small'>
						<InputLabel id='copy-from-label'>Select worker to copy from</InputLabel>
						<Select
							labelId='copy-from-label'
							value={selectedCopyFromLaborerId ?? ''}
							label='Select worker to copy from'
							onChange={(e) =>
								setSelectedCopyFromLaborerId(
									e.target.value === '' ? undefined : (e.target.value as number),
								)
							}
						>
							{laborers?.map((worker) => (
								<MenuItem key={worker.id} value={worker.id}>
									{worker.fullName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{selectedCopyFromLaborerId != null && (
						<Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
							Tasks and time records from the selected worker will be copied to each new timesheet.
						</Typography>
					)}
				</Box>
			</ConfirmDialog>
		</>
	)
}

export default ShiftEndWorkersPartialPage
